import { Formik, Form } from "formik";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";

import { useState } from "react";
import dayjs from "dayjs";
import { Autocomplete, Card, Grid, Icon, TextField } from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers";
import { useMutation, useQuery } from "@tanstack/react-query";

import { useCustomerValue } from "providers/CustomerContext";
import { getPORNumberOption } from "services/data.inbound.service";
import { getStandardReport } from "services/data.report.service";
import { InboundLayout } from "ui-components/Layout";
import { LoadingDialog } from "ui-components/Alert";
import { getRecvCodeOption } from "services/data.report.service";
import { Error_ } from "ui-components/Alert";
import { SelectFields } from "ui-components/Form";
import { CustomDatePicker } from "ui-components/CustomInput";
import { downloadPDF2 } from "layouts/views/common/file";
import { downloadFile } from "layouts/views/common/file";
import { useTeamValue } from "providers/TeamContext";
import { ReadonlyText } from "ui-components/CustomInput";

function POReport() {
  const { permissions, allowAll } = useTeamValue();
  const { customerCode } = useCustomerValue();
  const [alert, setAlert] = useState(null);
  const onError = (data) => setAlert(<Error_ content="Invalid Request" onChange={setAlert} />);
  const exportPermission =
    allowAll ||
    (Boolean(permissions["PO Receiving Report_Report"]) &&
      permissions["PO Receiving Report_Report"].findIndex((b) => b === "export") !== -1);

  const downloadReportMutation = useMutation(getStandardReport, {
    onSuccess: (response) => {
      if (reportType === "PDF") {
        downloadPDF2(response.data,`${recvCode}.pdf`);
      } else {
        downloadFile(response.data, `porecv.csv`);
      }
    },
    onError: async (data) => {
      let content = await data.response.data.text();
      if (content.includes("No data")) {
        content = "No data";
      }
      setAlert(<Error_ content={content} onChange={setAlert} />);
    },
  });

  const [recvCodeOptions, setRecvCodeOptions] = useState([]);
  const [recvCode, setRecvCode] = useState(null);
  const date = dayjs(new Date()).format("YYYY-MM-DD");
  const [dateFrom, setDateFrom] = useState(date);
  const [dateTo, setDateTo] = useState(date);
  const [reportType, setReportType] = useState("PDF");

  const handleSubmit = () => {
    setAlert(null);
    const valid = dayjs(dateTo).isSame(dayjs(dateFrom)) || dayjs(dateTo).isAfter(dayjs(dateFrom));
    if (!Boolean(recvCode) && !valid) {
      setAlert(<Error_ content="Start date cannot greater than end date" onChange={setAlert} />);
      return;
    }

    if (dayjs(date).isBefore(dateTo)) {
      setAlert(<Error_ content="End date cannot greater than today date" onChange={setAlert} />);
      return;
    }
    const startDate = dayjs(dateFrom).format("YYYY-MM-DD");
    const endDate = dayjs(dateTo).format("YYYY-MM-DD");
    downloadReportMutation.mutate({
      customerCode,
      recvCode,
      dateFrom: startDate,
      dateTo: endDate,
      reportType,
    });
  };

  useQuery({
    queryKey: ["report-po", { customerCode }],
    queryFn: () => getRecvCodeOption({ customerCode, recvType: "PORECV" }),
    enabled: Boolean(customerCode),
    onSuccess: (data) => {
      setRecvCode(null);
      setRecvCodeOptions(data.data.data);
    },
  });

  const downloadStatus = downloadReportMutation.status === "loading";

  return (
    <InboundLayout>
      <Card sx={{ height: "100%" }}>
        {downloadStatus && <LoadingDialog />}
        <MDBox p={3}>
          {alert}
          <MDBox pb={3}>
            <MDTypography color="info" variant="h3">
              Export Standard / Normal Report
            </MDTypography>
          </MDBox>
          <Grid container spacing={3}>
            <Grid item xs={6}>
              <SelectFields
                field={{
                  name: "recvCode",
                  label: "Recv Code",
                  value: recvCode,
                  errors: {},
                }}
                options={recvCodeOptions}
                onChange={(e, v) => setRecvCode(v)}
              />
            </Grid>
            <Grid item xs={6}>
              <SelectFields
                field={{
                  name: "reportType",
                  label: "Export to File Type *",
                  value: reportType,
                  errors: {},
                  required: true,
                }}
                options={["PDF", "CSV"]}
                onChange={(e, v) => setReportType(v)}
              />
            </Grid>
          </Grid>
          <Grid container spacing={3}>
            <Grid item xs={6}>
              <CustomDatePicker
                field={{
                  name: "startDate",
                  label: "Start Date",
                  value: dateFrom,
                  onChange: setDateFrom,
                  disableClearable: true,
                }}
              />
            </Grid>
            <Grid item xs={6}>
              <CustomDatePicker
                field={{
                  name: "endDate",
                  label: "End Date",
                  value: dateTo,
                  onChange: setDateTo,
                  disableClearable: true,
                }}
              />
            </Grid>
          </Grid>

          <MDBox mt={2} display="flex" justifyContent="center">
            <MDButton
              type="submit"
              variant="gradient"
              color="info"
              disabled={!exportPermission}
              onClick={handleSubmit}
            >
              Download
            </MDButton>
          </MDBox>
        </MDBox>
      </Card>
    </InboundLayout>
  );
}

export default POReport;
